'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowLeft } from 'lucide-react';

export default function OmPage() {
  return (
    <div className="min-h-screen bg-[#FDFAF5] font-sans overflow-x-hidden">
      {/* Background blobs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[400px] h-[400px] rounded-full bg-blue-100/30 blur-3xl" />
        <div className="absolute bottom-0 -left-40 w-[350px] h-[350px] rounded-full bg-amber-100/25 blur-3xl" />
      </div>

      {/* NAV */}
      <nav className="relative z-10 max-w-3xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link
          href="/landing"
          className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#1E2D4A] font-medium transition-colors">

          <ArrowLeft size={15} />
          Tilbage
        </Link>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-xl overflow-hidden">
            <Image
              src="/assets/images/GembaKonceptNoText-1773955908851.png"
              alt="TeamGemba logo"
              width={28}
              height={28}
              className="object-cover w-full h-full" />

          </div>
          <span className="font-bold text-[#1E2D4A] text-sm tracking-tight">TeamGemba</span>
        </div>
      </nav>

      {/* CONTENT */}
      <main className="relative z-10 max-w-2xl mx-auto px-6 pt-8 pb-20">

        {/* Founder photo placeholder */}
        <div className="mb-8 flex justify-center">
          <div className="w-64 h-64 rounded-full border-4 border-white shadow-lg overflow-hidden">
            <Image
              src="/assets/images/Adobe_Express_-_file-1774187446980.jpg"
              alt="Ludwig, grundlægger af TeamGemba"
              width={400}
              height={400}
              className="object-cover w-full h-full" />

          </div>
        </div>

        {/* Headline */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#1E2D4A] mb-6 text-center leading-tight">
          Hej, mit navn er Ludwig
        </h1>

        {/* Story */}
        <div className="space-y-5 text-[#4A5568] leading-relaxed text-base">
          <p>
            Jeg er 19 år gammel, og jeg står bag TeamGemba.

Idéen startede egentlig et helt andet sted. Jeg har længe haft en ambition om at bygge en større platform – noget, der kunne samle mange funktioner ét sted og gøre hverdagen nemmere for teams. Men undervejs stødte jeg på en problemstilling, som føltes langt mere konkret og vigtig at løse først.
          </p>

          <p>
            Jeg begyndte at lægge mærke til, hvor svært det egentlig er at vide, hvordan et team har det – ikke på overfladen, men ægte. Ikke fordi folk ikke vil hinanden det godt, men fordi hverdagen hurtigt bliver travl, og de små signaler nemt bliver overset. Ofte opdager man først problemerne, når de allerede har sat sig i stemningen, samarbejdet eller i værste fald sygefraværet.
          </p>

          <p>
            Det var her idéen til TeamGemba opstod.
I stedet for at bygge noget stort og komplekst fra starten, valgte jeg at fokusere på én ting: At gøre det nemt at forstå, hvordan et team har det – løbende og ærligt.
          </p>

          <div className="bg-amber-50 border border-amber-200/50 rounded-2xl px-6 py-5">
            <p className="text-[#1E2D4A] font-medium italic">
              TeamGemba er derfor ikke lavet til at &quot;fikse&quot; problemer, når de opstår. Det er lavet til at opdage dem i tide. Med simple, ugentlige check-ins og et klart overblik bliver det muligt at se udviklingen i et team, før små udfordringer vokser sig større.
            </p>
          </div>

          <p>
            Målet er ikke at gøre det kompliceret, men tværtimod at holde det simpelt nok til, at det faktisk bliver brugt – og gør en reel forskel.

Jeg bygger stadig videre på TeamGemba, og der er mange idéer for fremtiden. Men kernen vil altid være den samme:
At hjælpe teams med at forstå sig selv lidt bedre – før det bliver et problem.
          </p>

          <p className="text-sm text-sand-400">
            — Ludwig Stræde Bode, grundlægger af TeamGemba
          </p>
          <a
            href="https://www.linkedin.com/in/ludwig-bode-0968a9309/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 mt-2 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-3.5 h-3.5"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            Se min LinkedIn
          </a>
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link
            href="/landing"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold rounded-2xl shadow-warm-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200">

            Prøv TeamGemba
          </Link>
          <p className="mt-3 text-xs text-sand-400">Tidlig adgang</p>
        </div>
      </main>
    </div>);

}